import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  FileSpreadsheet, FileText, Printer, Loader2, BarChart3, Users, Building2, ShieldCheck, Cake,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ============== REPORTS PANEL ==============
export function SeatReportsPanel({ events }) {
  const api = useApi();
  const [eventId, setEventId] = useState('');
  const [threshold, setThreshold] = useState(40);
  const [clearanceFilter, setClearanceFilter] = useState('all');
  const [preview, setPreview] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState('');

  const eventParam = eventId ? `&event_id=${encodeURIComponent(eventId)}` : '';

  const loadPreview = useCallback(async (type) => {
    setLoading(true);
    setPreview(null);
    setPreviewTitle('');
    try {
      let url = '', title = '';
      if (type === 'floor-wise') { url = `/seat/reports/floor-wise?format=json${eventParam}`; title = 'Floor-wise Bookings'; }
      else if (type === 'booking-summary') { url = `/seat/reports/booking-summary?format=json${eventParam}`; title = 'Booking Summary'; }
      else if (type === 'clearance') { url = `/seat/reports/clearance?format=json${clearanceFilter !== 'all' ? '&status=' + clearanceFilter : ''}`; title = 'HOF Clearance Report'; }
      else if (type === 'age-wise') { url = `/seat/reports/age-wise?format=json&threshold=${threshold}${eventParam}`; title = `Age-wise (≥${threshold})`; }
      const res = await api.get(url);
      setPreview({ type, data: res.data });
      setPreviewTitle(title);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [eventId, clearanceFilter, threshold]);

  const download = async (type, format) => {
    const key = `${type}-${format}`;
    setBusyKey(key);
    try {
      let path = '';
      if (type === 'floor-wise') path = `/seat/reports/floor-wise?format=${format}${eventParam}`;
      else if (type === 'booking-summary') path = `/seat/reports/booking-summary?format=${format}${eventParam}`;
      else if (type === 'clearance') path = `/seat/reports/clearance?format=${format}${clearanceFilter !== 'all' ? '&status=' + clearanceFilter : ''}`;
      else if (type === 'age-wise') path = `/seat/reports/age-wise?format=${format}&threshold=${threshold}${eventParam}`;
      const res = await api.get(path, { responseType: 'blob' });
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const mime = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      downloadBlob(new Blob([res.data], { type: mime }), `${type}_${new Date().toISOString().slice(0, 10)}.${ext}`);
    } catch (e) { toast.error('Download failed'); }
    finally { setBusyKey(''); }
  };

  const printPreview = () => {
    if (!preview) { toast.error('Click Preview first'); return; }
    const rows = preview?.data?.rows || [];
    const headers = rows.length ? Object.keys(rows[0]) : [];
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>${previewTitle}</title>
      <style>body{font-family:Arial,sans-serif;padding:14px;color:#111}
      h1{font-size:18px;margin:0 0 8px}
      table{border-collapse:collapse;width:100%;font-size:11px}
      th{background:#0B2545;color:#fff;padding:6px;text-align:left}
      td{padding:5px 6px;border:0.5pt solid #ccc}
      tr:nth-child(even){background:#f6f6f6}
      </style></head><body>
      <h1>${previewTitle}</h1>
      <div style="font-size:11px;color:#555;margin-bottom:8px">Generated ${new Date().toLocaleString()}</div>
      <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${rows.map(r => `<tr>${headers.map(h => `<td>${(r[h] ?? '')}</td>`).join('')}</tr>`).join('')}</tbody></table>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)<\/script>
      </body></html>`);
    w.document.close();
  };

  const reportTypes = [
    { key: 'floor-wise', label: 'Floor-wise Bookings', icon: Building2, color: 'text-blue-700 bg-blue-100',
      desc: 'Every booking by floor — seat #, member, ITS, HOF, type.' },
    { key: 'booking-summary', label: 'Booking Summary', icon: BarChart3, color: 'text-indigo-700 bg-indigo-100',
      desc: 'Per-floor counts: total / booked / available.' },
    { key: 'clearance', label: 'HOF Clearance Report', icon: ShieldCheck, color: 'text-emerald-700 bg-emerald-100',
      desc: 'Cleared vs pending HOFs with eligible-ladies count.' },
    { key: 'age-wise', label: `Age-wise (≥${threshold})`, icon: Cake, color: 'text-amber-700 bg-amber-100',
      desc: `Bookings split by age threshold.` },
  ];

  return (
    <div className="space-y-4">
      {/* Common filters */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Label className="text-xs font-medium">Event:</Label>
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm bg-white"
            data-testid="reports-event-select"
          >
            <option value="">All events</option>
            {(events || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <div className="hidden sm:block w-px h-6 bg-slate-200 mx-1" />
          <Label className="text-xs font-medium">Age threshold:</Label>
          <Input type="number" min={1} max={120} value={threshold}
            onChange={(e) => setThreshold(parseInt(e.target.value, 10) || 40)}
            className="w-20 h-8 text-sm" data-testid="reports-age-threshold" />
          <div className="hidden sm:block w-px h-6 bg-slate-200 mx-1" />
          <Label className="text-xs font-medium">Clearance:</Label>
          <div className="inline-flex rounded-md border bg-white">
            {[['all', 'All'], ['pending', 'Pending'], ['approved', 'Approved']].map(([v, l]) => (
              <button key={v} onClick={() => setClearanceFilter(v)}
                className={`px-2 py-1 text-xs ${clearanceFilter === v ? 'bg-emerald-600 text-white' : 'text-slate-700'}`}
              >{l}</button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Report cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {reportTypes.map(rt => (
          <Card key={rt.key} className="hover:shadow-md transition-shadow" data-testid={`report-card-${rt.key}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-md flex items-center justify-center ${rt.color}`}>
                  <rt.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{rt.label}</div>
                  <div className="text-xs text-slate-500">{rt.desc}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant="outline" onClick={() => loadPreview(rt.key)} disabled={loading} data-testid={`preview-${rt.key}`}>
                  {loading && preview?.type === rt.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                  Preview
                </Button>
                <Button size="sm" variant="outline" onClick={() => download(rt.key, 'excel')} disabled={busyKey === `${rt.key}-excel`} data-testid={`excel-${rt.key}`}>
                  {busyKey === `${rt.key}-excel` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 mr-1 text-green-600" />}
                  Excel
                </Button>
                <Button size="sm" variant="outline" onClick={() => download(rt.key, 'pdf')} disabled={busyKey === `${rt.key}-pdf`} data-testid={`pdf-${rt.key}`}>
                  {busyKey === `${rt.key}-pdf` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 mr-1 text-red-600" />}
                  PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Preview area */}
      {preview && (
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h3 className="font-semibold flex-1">{previewTitle}</h3>
              <Badge variant="outline">{(preview.data?.rows || []).length} rows</Badge>
              <Button size="sm" variant="outline" onClick={printPreview} data-testid="reports-print">
                <Printer className="w-3 h-3 mr-1" /> Print
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>Close</Button>
            </div>
            <PreviewTable rows={preview.data?.rows || []} totals={preview.data?.totals} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const PreviewTable = ({ rows, totals }) => {
  if (!rows.length) return <div className="text-sm text-slate-500 py-6 text-center">No data</div>;
  const headers = Object.keys(rows[0]);
  return (
    <div className="border rounded overflow-hidden">
      <div className="max-h-96 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-700 uppercase tracking-wide sticky top-0">
            <tr>{headers.map(h => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r, i) => (
              <tr key={i} className={r.Floor === 'TOTAL' || r.Status === 'TOTAL' ? 'font-bold bg-slate-50' : 'hover:bg-slate-50'}>
                {headers.map(h => <td key={h} className="px-2 py-1.5">{r[h] ?? ''}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totals && (
        <div className="bg-slate-50 px-3 py-2 text-xs border-t flex flex-wrap gap-3 text-slate-700">
          {Object.entries(totals).map(([k, v]) => (
            <span key={k}><strong>{k}:</strong> {v}</span>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== AUDIT LOGS PANEL ==============
export function SeatAuditPanel() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '100'); params.set('page', String(page));
      if (filterAction !== 'all') params.set('action', filterAction);
      if (q.trim()) params.set('q', q.trim());
      const res = await api.get(`/seat/audit-logs?${params.toString()}`);
      setRows(res.data?.rows || []);
      setActions(res.data?.actions || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Failed to load audit logs'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [filterAction, q, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Input placeholder="Search who / target / IP…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} className="flex-1 min-w-[200px]" data-testid="audit-search" />
          <select value={filterAction} onChange={(e) => { setPage(1); setFilterAction(e.target.value); }}
            className="border rounded px-2 py-1.5 text-sm bg-white" data-testid="audit-action-filter">
            <option value="all">All actions</option>
            {actions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <Badge variant="outline" className="px-2 py-1">{total} total</Badge>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">No audit entries match</div>
          ) : (
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left">When</th>
                    <th className="px-2 py-2 text-left">Action</th>
                    <th className="px-2 py-2 text-left">Who</th>
                    <th className="px-2 py-2 text-left">Target</th>
                    <th className="px-2 py-2 text-left hidden md:table-cell">Details</th>
                    <th className="px-2 py-2 text-left">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map(r => (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`audit-row-${r.id}`}>
                      <td className="px-2 py-1.5 whitespace-nowrap">{(r.at || '').replace('T', ' ').split('.')[0]}</td>
                      <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{r.action}</Badge></td>
                      <td className="px-2 py-1.5 max-w-[180px] truncate" title={r.who}>{r.who}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px] max-w-[160px] truncate" title={r.target}>{r.target || ''}</td>
                      <td className="px-2 py-1.5 hidden md:table-cell max-w-[260px] truncate text-slate-500" title={JSON.stringify(r.details)}>
                        {r.details && Object.keys(r.details).length ? Object.entries(r.details).map(([k, v]) => `${k}=${v}`).join(' · ') : ''}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{r.ip || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
        <span className="text-xs text-slate-500">Page {page}</span>
        <Button size="sm" variant="outline" disabled={rows.length < 100} onClick={() => setPage(p => p + 1)}>Next</Button>
      </div>
    </div>
  );
}
